import { useState } from 'react';
import { isEmailValid } from 'helpers/user';
import { loginUser } from 'stores/userStore';
import EyeSlashIcon from 'icons/EyeSlashIcon';
import useToggleable from 'hooks/useToggleable';
import { useApplicationContainer } from 'containers/ApplicationContainer';
import Button, { ButtonWidth } from '../Button';
import TextInput from '../inputs/TextInput';
import Spinner from '../Spinner';
import styles from './styles/Form.module.scss';
import AuthModalHeader from './AuthModalHeader';
import { getAuthModalTypeFromPath } from './AuthModalV2';

export default function LoginForm({ register, forgotPassword }: { register: () => void; forgotPassword: () => void }) {
  const [{ path }, { handleCloseAuthModal, handleOpenLink }] = useApplicationContainer();
  const [email, setEmail] = useState<string | undefined>('');
  const [password, setPassword] = useState<string | undefined>('');

  const [clientSideEmailError, setClientSideEmailError] = useState<boolean>(false);
  const [clientSidePasswordError, setClientSidePasswordError] = useState<boolean>(false);

  const [showPassword, { handleToggle: handleToggleShowPassword }] = useToggleable(false);

  const [errors, setErrors] = useState<string[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loginRoute = getAuthModalTypeFromPath(path);

  const emailValid = email && email.length > 3 && isEmailValid(email);
  const passwordValid = password && password.length > 6;

  const handleSignIn = async () => {
    const clientErrors = [];
    if (!emailValid) {
      setClientSideEmailError(true);
      clientErrors.push('email');
    }
    if (!passwordValid) {
      setClientSidePasswordError(true);
      clientErrors.push('password');
    }
    if (!emailValid || !passwordValid) {
      setErrors([`Invalid ${clientErrors.join(', ')}.`]);
      return;
    }
    setIsSaving(true);

    try {
      await loginUser(email, password);
      loginRoute ? handleOpenLink('/') : handleCloseAuthModal();
    } catch (errors) {
      setErrors(errors as string[]);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <AuthModalHeader />
      <div className={styles.Form}>
        <div className={styles.Container}>
          <label htmlFor="email">Email</label>
          <TextInput
            onChange={val => setEmail(val)}
            value={email}
            hasError={Boolean(errors) || clientSideEmailError}
            placeholder="Email address"
          />
        </div>
        <div className={`${styles.Container} ${styles.PasswordContainer}`}>
          <label htmlFor="password">Password</label>
          <TextInput
            onChange={val => setPassword(val)}
            value={password}
            type={showPassword ? 'text' : 'password'}
            hasError={Boolean(errors) || clientSidePasswordError}
            placeholder="Password"
          />
          <div className={styles.PasswordToggle} onClick={handleToggleShowPassword}>
            <EyeSlashIcon />
          </div>
        </div>
        <div onClick={forgotPassword} className={`${styles.Secondary} ${styles.ForgotPassword}`}>
          {errors && errors.length && <span className={styles.Error}>{errors.toString()}</span>}
          <span>Forgot Password?</span>
        </div>
        <Button width={ButtonWidth.FULL} className={styles.LogInButton} onClick={handleSignIn}>
          {isSaving ? <Spinner /> : 'Log In'}
        </Button>
        <div className={styles.Secondary}>
          Don't have an SBLive Sports account?&nbsp;<span onClick={register}>Sign Up</span>
        </div>
      </div>
    </>
  );
}
